import React, { useContext } from "react";
import { UserContext } from "../../../Data/UserContext";
import { resolveAvatarFrameSrc } from "../../Profile/avatarFrame";
import { AvatarWithXpCrown } from "../../Xp/AvatarWithXpCrown";

const OVERLAY_ROOT_CLASS =
    "absolute -bottom-4 right-0 z-12 flex translate-y-[5px] items-center overflow-hidden border border-[#1B062F]/30 bg-[#6432A5] shadow-[0_4px_12px_rgba(0,0,0,0.25)] md:-bottom-5 md:translate-y-[5px]";

const INLINE_ROOT_CLASS =
    "flex flex-shrink-0 items-center overflow-hidden border border-[#1B062F]/30 bg-[#6432A5] h-[32px] md:h-[34px]";

const INLINE_COMPACT_ROOT_CLASS =
    "flex flex-shrink-0 items-center overflow-hidden border border-[#1B062F]/30 bg-[#6432A5] h-[26px] md:h-[28px]";

/** Match-card header badge — avatar in orange tile + “N GAMES” (Figma). */
const HEADER_ROOT_CLASS =
    "flex h-[24px] min-h-[24px] max-h-[24px] flex-shrink-0 items-stretch overflow-hidden bg-[#9747FF] shadow-[0px_0px_5px_0px_#00000099]";

export type LobbyActiveGamesStickerProps = {
    count: number;
    avatarSrc?: string;
    xpWeekCrown?: string | null;
    /** Defaults to the signed-in user's frame (the sticker always shows the viewer's own avatar). */
    avatarFrameUrl?: string | null;
    avatarFrameSmallUrl?: string | null;
    labelSingular?: string;
    labelPlural?: string;
    interactive?: boolean;
    onActivate?: (event: React.SyntheticEvent) => void;
    ariaLabel?: string;
    /** overlay = lobby header badge; inline = matches footer row; header = compact match-card header */
    layout?: "overlay" | "inline" | "header";
    /** Smaller inline badge without text label — for compact match-card footers. */
    compact?: boolean;
};

/** Lobby match-card badge: avatar + count + “GAME(S)” — shared by Bullion, Squares, and Trader lobbies. */
export function LobbyActiveGamesSticker({
    count,
    avatarSrc = "/img/avatar.png",
    xpWeekCrown = null,
    avatarFrameUrl,
    avatarFrameSmallUrl,
    labelSingular = "Game",
    labelPlural = "Games",
    interactive = false,
    onActivate,
    ariaLabel,
    layout = "overlay",
    compact = false,
}: LobbyActiveGamesStickerProps) {
    const viewer = useContext(UserContext)?.user;
    const n = Math.max(0, Math.floor(Number(count) || 0));
    if (n <= 0) return null;

    const label = (n === 1 ? labelSingular : labelPlural).toUpperCase();
    const defaultAria = `${n} active ${n === 1 ? labelSingular.toLowerCase() : labelPlural.toLowerCase()}`;

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!interactive || !onActivate) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate(e);
        }
    };

    const handleClick = (e: React.MouseEvent) => {
        if (!interactive || !onActivate) return;
        e.preventDefault();
        e.stopPropagation();
        onActivate(e);
    };

    const rootClass =
        layout === "header"
            ? HEADER_ROOT_CLASS
            : layout === "inline"
              ? compact
                  ? INLINE_COMPACT_ROOT_CLASS
                  : INLINE_ROOT_CLASS
              : OVERLAY_ROOT_CLASS;
    const avatarSizeClass =
        layout === "header"
            ? "h-[18px] w-[18px]"
            : layout === "inline" && compact
              ? "h-[20px] w-[20px]"
              : "h-[24px] w-[24px]";
    /** Header (18px) and compact inline (20px) avatars are too small for a frame. */
    const frameSrc =
        layout === "header" || (layout === "inline" && compact)
            ? null
            : resolveAvatarFrameSrc(
                  "compact",
                  avatarFrameUrl !== undefined ? avatarFrameUrl : viewer?.avatar_frame_url,
                  avatarFrameSmallUrl !== undefined ? avatarFrameSmallUrl : viewer?.avatar_frame_small_url,
              );
    const countSizeClass =
        layout === "inline" && compact
            ? "inline-flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center bg-[#F73261] font-anton text-[13px] font-normal uppercase leading-none text-white"
            : "inline-flex h-[24px] w-[24px] flex-shrink-0 items-center justify-center bg-[#F73261] font-anton text-[16px] font-normal uppercase leading-[13.33px] text-white";

    if (layout === "header") {
        return (
            <div
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                onClick={interactive ? handleClick : undefined}
                onKeyDown={interactive ? handleKeyDown : undefined}
                className={`${rootClass} ${interactive ? "cursor-pointer" : "cursor-default"}`}
                aria-label={
                    ariaLabel ??
                    (interactive
                        ? `${n} ${n === 1 ? labelSingular.toLowerCase() : labelPlural.toLowerCase()} — open My Games`
                        : defaultAria)
                }
            >
                <div className="flex h-[24px] w-[24px] flex-shrink-0 items-center justify-center bg-[#FF8A3D]">
                    <AvatarWithXpCrown crown={xpWeekCrown} className={`block flex-shrink-0 ${avatarSizeClass}`}>
                        <img
                            src={avatarSrc}
                            alt=""
                            className="h-full w-full rounded-full border border-white object-cover"
                            draggable={false}
                        />
                    </AvatarWithXpCrown>
                </div>
                <span
                    className="flex flex-shrink-0 items-center whitespace-nowrap px-2 font-anton text-[11px] font-normal uppercase leading-none text-white"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                >
                    {n} {label}
                </span>
            </div>
        );
    }

    return (
        <div
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={interactive ? handleClick : undefined}
            onKeyDown={interactive ? handleKeyDown : undefined}
            className={`${rootClass} ${interactive ? "cursor-pointer" : layout === "inline" ? "cursor-default" : "pointer-events-none cursor-default"}`}
            aria-label={ariaLabel ?? (interactive ? undefined : defaultAria)}
        >
            <div className="flex flex-shrink-0 items-center justify-center p-0.5">
                <AvatarWithXpCrown crown={xpWeekCrown} frameSrc={frameSrc} className={`block flex-shrink-0 ${avatarSizeClass}`}>
                <div className="flex h-full w-full flex-shrink-0 items-center justify-center bg-[#6432A5]">
                    <img
                        src={avatarSrc}
                        alt=""
                        className="h-full w-full rounded-full object-cover"
                        draggable={false}
                    />
                </div>
                </AvatarWithXpCrown>
            </div>
            <div className="flex flex-shrink-0 items-center justify-center p-0.5">
                <span
                    className={countSizeClass}
                    style={{
                        letterSpacing: "0%",
                        textAlign: "center",
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {n}
                </span>
            </div>
            {layout === "inline" && compact ? null : (
                <span
                    className="inline-flex h-[24px] flex-shrink-0 items-center justify-end bg-[#6432A5] pl-[5px] pr-[11px] font-anton text-[13.33px] font-normal uppercase leading-[14.67px] text-white"
                    style={{ letterSpacing: "0%" }}
                >
                    {label}
                </span>
            )}
        </div>
    );
}
